import {
	Box,
	Grid,
	GridItem,
	Heading,
	HStack,
	IconButton,
	Image,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Text,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	Button,
	useDisclosure,
	Switch,
	VStack,
	Flex,
	Spacer,
	Icon,
	Spinner
} from "@chakra-ui/react";
import logo from "../../../assets/images/logo.png";
import { FunctionComponent, ReactElement, useEffect, useState } from "react";
import { MdAccountCircle, MdEmail } from "react-icons/md";
import { useAuth } from "../../../hooks/useAuth";
import { User } from "../../../utils/types";
import useClaimsToast from "../../../hooks/useToast";
import { Link, useNavigate } from "react-router-dom";
import {
	getProvider,
	getProviderNotificationPreferences,
	updateProviderNotificationPreferences
} from "../../../utils/api/providers";
import { UserRole } from "../../../utils/consts";
import { RouterPathMapping } from "../../../Router";
import { createAttorneyUpdateLinkFromEnv } from "../../../utils/helpers";
import { Provider } from "../../../utils/api/protos/messages/providers.message_pb";
import { RpcError } from "grpc-web";
import { ExternalLinkIcon } from "@chakra-ui/icons";

interface HeaderProps {}

/**
 * Handles the header bar displayed at the top of the common layout
 */
const Header: FunctionComponent<HeaderProps> = (props: HeaderProps): ReactElement => {
	const { hasRole, getUser, logout } = useAuth();
	const toast = useClaimsToast();
	const navigate = useNavigate();
	const user: User | undefined = getUser();
	const { isOpen: isPrefOpen, onOpen: onOpenPref, onClose: onClosePref } = useDisclosure();

	// Preference toggles
	const [prefApproved, setPrefApproved] = useState<boolean>(false);
	const [prefInReview, setPrefInReview] = useState<boolean>(false);
	const [prefQueued, setPrefQueued] = useState<boolean>(false);
	const [prefClosedPaid, setPrefClosedPaid] = useState<boolean>(false);

	const [isLoadingPrefs, setIsLoadingPrefs] = useState<boolean>(false);
	const [isProviderFetchLoading, setIsProviderFetchLoading] = useState<boolean>(false);

	// Load preferences when modal is opened
	useEffect(() => {
		if (isPrefOpen) {
			setIsLoadingPrefs(true);
			getProviderNotificationPreferences(user!.id)
				.then((response) => {
					setPrefApproved(response.getOnApproved());
					setPrefInReview(response.getOnInReview());
					setPrefQueued(response.getOnQueued());
					setPrefClosedPaid(response.getOnClosedPaid());
				})
				.catch((err) => {
					console.error("Failed to load preferences:", err);
				})
				.finally(() => {
					setIsLoadingPrefs(false);
				});
		}
	}, [isPrefOpen]);

	const handlePreferenceChange = (preferenceType: string, value: boolean): void => {
		switch (preferenceType) {
			case "approved":
				setPrefApproved(value);
				break;
			case "inReview":
				setPrefInReview(value);
				break;
			case "queued":
				setPrefQueued(value);
				break;
			case "closedPaid":
				setPrefClosedPaid(value);
				break;
			default:
				break;
		}

		updateProviderNotificationPreferences(
			user!.id,
			preferenceType === "approved" ? value : prefApproved,
			preferenceType === "inReview" ? value : prefInReview,
			preferenceType === "queued" ? value : prefQueued,
			preferenceType === "closedPaid" ? value : prefClosedPaid
		).catch((err) => {
			console.error("Failed to update preferences:", err);
			toast("Error", "There was a problem updating preferences", "error");
		});
	};

	const handleLogout = async (): Promise<void> => {
		try {
			await logout();
			navigate("/login");
		} catch (err) {
			console.error("Logout error:", err);
			navigate("/login");
			toast("Warning", "Logged out locally. Please sign in again if needed.", "warning");
		}
	};

	const openLampAttorneyUpdateForm = async () => {
		setIsProviderFetchLoading(true);

		getProvider(+(user?.externalId ?? "0"))
			.then((response) => {
				const foundProvider: Provider | undefined = response.getProvider();
				if (foundProvider) {
					console.log("Found provider " + JSON.stringify(foundProvider.toObject()));
					const attorneyUpdateLink = createAttorneyUpdateLinkFromEnv(
						foundProvider.getAttyid(),
						foundProvider.getUpdateInfoToken()
					);
					// _blank opens the URL in a new tab
					// noopener and noreferrer are critical for security
					window.open(attorneyUpdateLink, "_blank", "noopener,noreferrer");
				} else {
					toast("Error", "Couldn't navigate to LAMP™, please contact customer service", "error");
				}
			})
			.catch((err: RpcError) => {
				console.error(err);
				toast("Error", "Couldn't navigate to LAMP™, please contact customer service", "error");
			})
			.finally(() => {
				setIsProviderFetchLoading(false);
			});
	};

	return (
		<Box my={"0"} borderColor="orange.400" borderBottomWidth="7px">
			<Grid marginY={3} marginX={5} alignItems="center" minHeight="49px">
				<GridItem
					display={"flex"}
					justifyContent={"space-between"}
					alignItems={"center"}
					margin={"auto"}
					width={"100%"}>
					<HStack width="200px">
						<Image src={logo} width="200px" />
					</HStack>
					<HStack width="200px">
						<Heading fontSize={"3xl"} textAlign={"center"} marginX={"auto"}>
							Claims Portal
						</Heading>
					</HStack>
					<HStack width="200px">
						<Menu>
							<MenuButton
								as={IconButton}
								aria-label="View profile"
								variant={"ghost"}
								icon={<MdAccountCircle color="gray.700" />}
								color="gray.700"
								fontSize={"30px"}
								marginLeft={"auto"}
							/>
							<MenuList zIndex={100}>
								<MenuItem isDisabled={true}>
									<Box>
										<Text>{user?.name}</Text>
										<Text fontSize="sm">{user?.role}</Text>
									</Box>
								</MenuItem>
								{hasRole([UserRole.Provider]) && (
									<>
										<MenuItem onClick={() => onOpenPref()}>
											<Box>
												<Text>Preferences</Text>
											</Box>
										</MenuItem>
										<MenuItem onClick={() => openLampAttorneyUpdateForm()}>
											<Box display="inline-flex">
												<Text>LAMP™ Profile</Text>
												{isProviderFetchLoading ? <Spinner size="sm" /> : <ExternalLinkIcon mx="2" mb="2px" />}
											</Box>
										</MenuItem>
									</>
								)}
								<MenuItem as={Link} to={RouterPathMapping.ReleaseNotes}>
									Release Notes
								</MenuItem>
								<MenuItem onClick={() => handleLogout()}>Log out</MenuItem>
							</MenuList>
						</Menu>
						{/* Preferences Modal */}
						<Modal isOpen={isPrefOpen} onClose={onClosePref} isCentered size="lg">
							<ModalOverlay />
							<ModalContent>
								<ModalHeader>Preferences</ModalHeader>
								<ModalCloseButton />
								<ModalBody>
									{isLoadingPrefs ? (
										<Flex direction="column" align="center" justify="center" py={8}>
											<Spinner size="lg" color="orange.400" mb={3} />
											<Text>Loading preferences...</Text>
										</Flex>
									) : (
										<VStack align="stretch" spacing={6}>
											<Flex align="center">
												<Icon as={MdEmail} boxSize={5} color="gray.600" mr={3} />
												<Text fontWeight="600">Claim Status Update Emails</Text>
											</Flex>

											<VStack align="start" spacing={4}>
												<Flex align="center">
													<Switch
														colorScheme="orange"
														isChecked={prefInReview}
														isDisabled={isLoadingPrefs}
														onChange={(e) => handlePreferenceChange("inReview", e.target.checked)}
														mr={4}
													/>
													<Text>In-Review</Text>
												</Flex>
												<Flex align="center">
													<Switch
														colorScheme="orange"
														isChecked={prefApproved}
														isDisabled={isLoadingPrefs}
														onChange={(e) => handlePreferenceChange("approved", e.target.checked)}
														mr={4}
													/>
													<Text>Approved</Text>
												</Flex>
												<Flex align="center">
													<Switch
														colorScheme="orange"
														isChecked={prefQueued}
														isDisabled={isLoadingPrefs}
														onChange={(e) => handlePreferenceChange("queued", e.target.checked)}
														mr={4}
													/>
													<Text>Queued For Payment</Text>
												</Flex>
												<Flex align="center">
													<Switch
														colorScheme="orange"
														isChecked={prefClosedPaid}
														isDisabled={isLoadingPrefs}
														onChange={(e) => handlePreferenceChange("closedPaid", e.target.checked)}
														mr={4}
													/>
													<Text>Closed - Paid</Text>
												</Flex>
											</VStack>
										</VStack>
									)}
								</ModalBody>

								<ModalFooter>
									<Spacer />
									<Button colorScheme="orange" size="md" onClick={onClosePref} isDisabled={isLoadingPrefs}>
										Close
									</Button>
								</ModalFooter>
							</ModalContent>
						</Modal>
					</HStack>
				</GridItem>
			</Grid>
		</Box>
	);
};

export default Header;
